import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import { Button, Dialog, TextField } from "@platform/design-system";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import {
  CORRECT_VALUE_REQUIRED,
  isEmptyValue,
  parseTypedValue,
} from "../shared/values";

import type { ReactNode } from "react";

export const BOTH_WRONG_DIALOG_TEST_ID = "golden-both-wrong";

const correctValueSchema = z.object({
  // The service stores no correction to an empty value (null, "", [] or {}).
  value: z
    .string()
    .refine(
      (text) => !isEmptyValue(parseTypedValue(text)),
      CORRECT_VALUE_REQUIRED,
    ),
});

type CorrectValue = z.infer<typeof correctValueSchema>;

/** Asks for the value a field should have had when RUMI and the analyst agreed on a wrong one. */
export const BothWrongDialog = ({
  fieldPath,
  open,
  onCancel,
  onCorrect,
}: {
  readonly fieldPath: string;
  readonly open: boolean;
  readonly onCancel: () => void;
  readonly onCorrect: (correctedValue: unknown) => void;
}): ReactNode => {
  const { control, handleSubmit, reset } = useForm<CorrectValue>({
    resolver: zodResolver(correctValueSchema),
    defaultValues: { value: "" },
  });

  const close = (): void => {
    reset();
    onCancel();
  };
  const onSubmit = handleSubmit(({ value }) => {
    reset();
    onCorrect(parseTypedValue(value));
  });

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Both are wrong"
      testIdSuffix="both-wrong"
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            data-testid={`${BOTH_WRONG_DIALOG_TEST_ID}-save`}
            onClick={() => void onSubmit()}
          >
            Store correction
          </Button>
        </>
      }
    >
      <Box
        component="form"
        noValidate
        onSubmit={(event) => void onSubmit(event)}
        sx={{ pt: 1 }}
      >
        {/* The design-system field forwards its ref to the wrapper, not the input,
            so it is controlled rather than registered. */}
        <Controller
          control={control}
          name="value"
          render={({ field: { ref: _ref, ...field }, fieldState }) => (
            <TextField
              data-testid={`${BOTH_WRONG_DIALOG_TEST_ID}-value`}
              label={`The correct value of ${fieldPath}`}
              helperText={
                fieldState.error?.message ??
                "Text, or JSON for a number, list, or object"
              }
              error={fieldState.error !== undefined}
              fullWidth
              {...field}
            />
          )}
        />
      </Box>
    </Dialog>
  );
};
